import { useState } from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import {
  BreakConfigurationFragment,
  TimePlanningPlannedBreakBreakTypeEnum as BreakType,
} from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import Callout from 'modules/core/components/Callout'
import TextField from 'modules/core/components/TextField'
import { useEffect } from 'modules/core/hooks/useEffect'
import i18n from 'modules/core/lib/i18n'

import EmployeesSection from 'modules/shift_management/components/ShiftManagement/EmployeesSection'
import PlannedBreaks from 'modules/shift_management/components/ShiftManagement/PlannedBreaks'
import PlannedBreaksSelector from 'modules/shift_management/components/ShiftManagement/PlannedBreaksSelector'
import ShiftLocationPicker from 'modules/shift_management/components/ShiftManagement/ShiftLocationPicker'
import ShiftNotesField from 'modules/shift_management/components/ShiftManagement/ShiftNotesField'
import ShiftOvertimeCheckbox from 'modules/shift_management/components/ShiftManagement/ShiftOvertimeCheckbox'
import ShiftTimePicker from 'modules/shift_management/components/ShiftManagement/ShiftTimePicker'
import TooltipIcon from 'modules/shift_management/components/ShiftManagement/TooltipIcon'
import { useEnabledFeatures } from 'modules/shift_management/hooks/useEnabledFeatures'
import type CommonSelectorsForm from 'modules/shift_management/lib/forms/CommonSelectorsForm'
import type PlannedBreakForm from 'modules/shift_management/lib/forms/PlannedBreakForm'
import type ShiftsEditForm from 'modules/shift_management/lib/forms/ShiftsEditForm'
import type PlannedBreak from 'modules/time_planning/store/internal/models/PlannedBreak'

interface Props {
  form: ShiftsEditForm
  commonSelectorsForm: CommonSelectorsForm
  mixedValuesByName: boolean
  mixedValuesByStartTime: boolean
  mixedValuesByEndTime: boolean
  mixedValuesByNotes: boolean
  mixedValuesByLocation: boolean
  mixedValuesByWorkArea: boolean
  hasMixedValues: boolean
  canReassignShifts: boolean
  selectableEmployeeIds: number[]
  showReassignMessage: boolean
  plannedBreakForms?: PlannedBreakForm[]
  readOnlyPlannedBreaks: PlannedBreak[] | undefined
  showBreaksForm: boolean
  onAddBreak: (breakConfiguration: BreakConfigurationFragment, breakType: BreakType) => void
  onDeleteBreak: (plannedBreakForm: PlannedBreakForm) => void
}

const EditShiftCustomSection = ({
  form,
  commonSelectorsForm,
  mixedValuesByName,
  mixedValuesByStartTime,
  mixedValuesByEndTime,
  mixedValuesByNotes,
  mixedValuesByLocation,
  mixedValuesByWorkArea,
  hasMixedValues,
  selectableEmployeeIds,
  showReassignMessage,
  canReassignShifts,
  plannedBreakForms,
  readOnlyPlannedBreaks,
  showBreaksForm,
  onAddBreak,
  onDeleteBreak,
}: Props) => {
  const [{ hasFeatureFlag }] = useEnabledFeatures()
  const [showMixedValuesCallout, setShowMixedValuesCallout] = useState<boolean>(hasMixedValues)
  const [showReassignCallout, setShowReassignCallout] = useState<boolean>(false)
  const selectedEmployees = commonSelectorsForm.employeeSelection?.ids ?? []
  useEffect(() => {
    if (showReassignMessage) {
      setShowReassignCallout(true)
    }
  }, [showReassignMessage])

  const locationsEnabled = hasFeatureFlag('dev_location_work_areas')
  const canEditBreaks = hasFeatureFlag('time_settings_breaks') && plannedBreakForms
  const isComplementaryHoursEnabled = hasFeatureFlag('dev_time_tracking_complementary_hours')

  const startTimeField = form.get('startAt')
  const endTimeField = form.get('endAt')
  const nameField = form.get('name').value.toString()
  const namePlaceholder = mixedValuesByName
    ? i18n.t('shift_management.edit_shifts.placeholder')
    : i18n.t('shift_management.actions_sidepanel.tooltip.name')

  return (
    <Box gap="s16">
      <EmployeesSection
        form={commonSelectorsForm}
        selectableEmployeeIds={selectableEmployeeIds}
        selectedEmployees={selectedEmployees}
        canReassignShifts={canReassignShifts}
        edit
      />
      {showReassignCallout && (
        <Callout
          type="info"
          description={i18n.t('shift_management.edit_shifts.reassignment_message')}
          onRemove={() => setShowReassignCallout(false)}
        />
      )}
      {showMixedValuesCallout && (
        <Callout
          type="info"
          title={i18n.t('shift_management.actions_sidepanel.mixed_values.title')}
          description={i18n.t('shift_management.actions_sidepanel.mixed_values.description')}
          onRemove={() => setShowMixedValuesCallout(false)}
        />
      )}
      <Box gap="s16" flexDirection="row" justifyContent="center" alignItems="center">
        <TooltipIcon.Medium
          icon="align-text-left"
          label={i18n.t('shift_management.actions_sidepanel.tooltip.name')}
          color="grey700"
        />
        <TextField
          placeholder={namePlaceholder}
          value={nameField}
          onChange={(name) => form.get('name').set(name)}
        />
      </Box>
      <ShiftTimePicker
        startField={startTimeField}
        endField={endTimeField}
        mixedValues={mixedValuesByStartTime || mixedValuesByEndTime}
        newVersion
      />
      <ShiftOvertimeCheckbox
        form={form}
        newVersion
        isComplementaryHoursEnabled={isComplementaryHoursEnabled}
      />
      {canEditBreaks && showBreaksForm && (
        <PlannedBreaksSelector
          plannedBreakForms={plannedBreakForms}
          onAddBreak={onAddBreak}
          onDeleteBreak={onDeleteBreak}
          newVersion
        />
      )}
      {canEditBreaks && !showBreaksForm && readOnlyPlannedBreaks && (
        <PlannedBreaks plannedBreaks={readOnlyPlannedBreaks} showDeleted canManage newVersion />
      )}
      {locationsEnabled && (
        <ShiftLocationPicker
          locationField={form.get('locationId')}
          workAreaField={form.get('workAreaId')}
          mixedValuesLocation={mixedValuesByLocation}
          mixedValuesWorkArea={mixedValuesByWorkArea}
          newVersion
        />
      )}
      {locationsEnabled && (
        <ShiftNotesField
          notesField={form.get('notes')}
          mixedValues={mixedValuesByNotes}
          newVersion
        />
      )}
    </Box>
  )
}

export default observer(EditShiftCustomSection)
